import { Component, type ErrorInfo, type ReactNode } from 'react'

interface State {
  error: Error | null
}

/**
 * A render error anywhere below unmounts the whole React tree and leaves a blank
 * page — no header, no message, nothing to act on. That is what an unexpected API
 * payload did once. Catch it, keep the page, and say what broke.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error:', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <h1 className="text-xl font-semibold text-ink">Something broke while rendering</h1>
        <p className="mt-2 text-sm text-muted">
          The page failed to draw. This is a bug in the wiki, not in your session.
        </p>
        <pre className="mt-4 overflow-x-auto rounded-[var(--radius-sh)] bg-sunken px-3 py-2 text-left font-mono text-xs text-danger">
          {error.message}
        </pre>
        <button
          onClick={() => location.reload()}
          className="mt-5 rounded-[var(--radius-sh)] bg-accent px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-hover"
        >
          Reload
        </button>
      </div>
    )
  }
}
